import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useCustomerSubmissionHandlers } from '@features/contacts/hooks/useCustomerSubmissionHandlers'
import { useUpdateContact } from '@features/contacts/hooks/useUpdateContact'
import { Contact } from '@features/contacts/types/contact'
import { CustomerForm } from '@features/contacts/types/customerForm'
import { getAddressExemptPayloadValue } from '@features/contacts/utils/getAddressExemptPayloadValue'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { getUpdateCustomerPayload } from '../utils/getUpdateCustomerPayload'
import { useInvoiceComplianceMode } from './useInvoiceComplianceMode'

interface CustomerFormSubmitProps {
  onSubmit: (customer: Contact) => void
}

export const useUpdateCustomerInformation = ({ onSubmit }: CustomerFormSubmitProps) => {
  const { t } = useTranslation()
  const { organization, isLoading: isOrganizationLoading } = useUserOrganization()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const { handleCustomerSubmitSuccess, handleCustomerSubmitError } = useCustomerSubmissionHandlers({
    onSubmit,
    successMessage: t('customer_modal.form.edit.notification.success'),
  })

  const { update: updateCustomer, isProcessing: isUpdatingCustomer } = useUpdateContact({
    onSuccess: handleCustomerSubmitSuccess,
    onError: handleCustomerSubmitError,
  })

  const submit = useCallback(
    (customerForm: CustomerForm) => {
      const addressExemptPayloadValue = getAddressExemptPayloadValue(customerForm, isFrenchComplianceMode)
      const customerPayload = getUpdateCustomerPayload(
        { ...customerForm, isAddressExempt: addressExemptPayloadValue },
        organization?.id,
      )
      updateCustomer(customerPayload)
    },
    [isFrenchComplianceMode, organization?.id, updateCustomer],
  )

  return {
    isLoading: isOrganizationLoading,
    isSubmitting: isUpdatingCustomer,
    submit,
  }
}
